<script setup lang="ts">
import moment from "moment-timezone";
import { useCrud } from "~/composables/use-crud";

defineOptions({ name: "CostEntryFormModal" });

const emit = defineEmits<{ (e: "saved"): void }>();

const { t } = useI18n();
const toast = useToast();

const isOpen = ref(false);

interface CostEntry {
  id: string;
  category: string;
  vendorName: string;
  description: string;
  amount: number;
  currency: string;
  periodMonth: string;
  status: string;
  paidTime: number;
}

const { formModel, formErrors, errorMsg, isCreate, saveLoading, save, load, reset } =
  useCrud<CostEntry>({
    endpoint: "/cost-entries",
    defaultFormModel: () => ({
      id: "",
      category: "other",
      vendorName: "",
      description: "",
      amount: 0,
      currency: "USD",
      periodMonth: moment().format("YYYY-MM"),
      status: "planned",
      paidTime: 0,
    }),
  });

// periodMonth is the accounting period a cost belongs to, and it stays a
// "2026-08" string: the cost pipelines range over it as text and slice the year
// out of it, so it is the stored shape. The picker works in unix seconds, so it
// gets the first of that month and hands one back.
const periodTime = computed({
  get: () =>
    formModel.periodMonth ? moment(formModel.periodMonth, "YYYY-MM").startOf("month").unix() : 0,
  set: (value: number) => {
    formModel.periodMonth = value ? moment.unix(value).format("YYYY-MM") : "";
  },
});

const isPaid = computed(() => formModel.status === "paid");

const categoryOptions = computed(() =>
  ["payroll", "infrastructure", "software", "marketing", "sales", "operations", "office", "tax", "other"].map((value) => ({
    value,
    label: t(`finance.cost.category_${value}`),
  })),
);

const currencyOptions = [
  { value: "USD", label: "USD" },
  { value: "VND", label: "VND" },
];

const statusOptions = computed(() => [
  { value: "planned", label: t("finance.cost.statusPlanned") },
  { value: "paid", label: t("finance.cost.statusPaid") },
]);

async function open(id?: string) {
  reset();
  if (id) await load(id);
  isOpen.value = true;
}

async function submit() {
  try {
    await save();
    toast.success({ title: t(isCreate.value ? "message.createSuccess" : "message.updateSuccess") });
    isOpen.value = false;
    emit("saved");
  } catch {
    // errors surface via errorMsg / formErrors
  }
}

defineExpose({ open });
</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="t(isCreate ? 'finance.cost.newTitle' : 'finance.cost.updateTitle')"
  >
    <div class="flex flex-col gap-medium">
      <InlineMessage v-if="errorMsg" variant="negative">{{ errorMsg }}</InlineMessage>

      <FormField
        v-model="formModel.vendorName"
        :label="t('finance.cost.vendor')"
        :error="formErrors.vendorName"
      />
      <FormSelect
        v-model="formModel.category"
        :label="t('finance.cost.category')"
        :options="categoryOptions"
        :error="formErrors.category"
      />
      <div class="flex flex-col gap-xsmall md:flex-row md:gap-small">
        <FormNumber
          v-model="formModel.amount"
          :label="t('finance.cost.amount')"
          :error="formErrors.amount"
          class="w-full md:w-1/2"
        />
        <FormSelect
          v-model="formModel.currency"
          :label="t('field.currency')"
          :options="currencyOptions"
          :error="formErrors.currency"
          class="w-full md:w-1/2"
        />
      </div>
      <div class="flex flex-col gap-xsmall md:flex-row md:gap-small">
        <FormDatePicker
          v-model="periodTime"
          format="MM/YYYY"
          :label="t('finance.cost.month')"
          :error="formErrors.periodMonth"
          class="w-full md:w-1/2"
        />
        <FormSelect
          v-model="formModel.status"
          :label="t('field.status')"
          :options="statusOptions"
          :error="formErrors.status"
          class="w-full md:w-1/2"
        />
      </div>

      <!-- Only a paid cost has a date the money moved; a planned one would just
           be an empty field inviting a guess. -->
      <FormDatePicker
        v-if="isPaid"
        v-model="formModel.paidTime"
        :label="t('finance.cost.paidTime')"
        :help-text="t('finance.cost.paidTimeHelp')"
        :error="formErrors.paidTime"
      />
      <FormTextarea
        v-model="formModel.description"
        :label="t('field.description')"
        :error="formErrors.description"
        :rows="3"
      />
    </div>

    <template #actions>
      <Button variant="tertiary" size="small" @click="isOpen = false">
        {{ t("action.cancel") }}
      </Button>
      <Button variant="primary" size="small" :disabled="saveLoading" @click="submit">
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
